﻿import { ReactNode, useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  Mail,
  MessageSquareWarning,
  Package,
  PackageX,
  Receipt,
  ShoppingBag,
  Sparkles,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import AdminLayout from "./AdminLayout";
import { AdminDashboard as AdminDashboardData, AdminUser, getAdminDashboard, getCurrentAdmin, logout } from "./api";

const ORDER_STATUS_LABEL: Record<string, string> = {
  Pending: "待處理",
  Processing: "處理中",
  Shipped: "已出貨",
  Completed: "已完成",
  Cancelled: "已取消",
};

const ORDER_STATUS_BADGE: Record<string, string> = {
  Pending: "bg-[#f3ecdf] text-[#8a6a2f]",
  Processing: "bg-[#e6eefb] text-[#2a5ca8]",
  Shipped: "bg-[#e9f4ec] text-[#245438]",
  Completed: "bg-[#e9f4ec] text-[#245438]",
  Cancelled: "bg-[#fdebea] text-[#9a2d2d]",
};

const ORDER_STATUS_BAR: Record<string, string> = {
  Pending: "bg-[#c99a3e]",
  Processing: "bg-[#2a5ca8]",
  Shipped: "bg-[#3f8a5c]",
  Completed: "bg-[#245438]",
  Cancelled: "bg-[#c2504f]",
};

const RESERVATION_STATUS_LABEL: Record<string, string> = {
  New: "新預約",
  Contacted: "已聯絡",
  Booked: "已預約",
  Closed: "已結案",
};

const RESERVATION_STATUS_BADGE: Record<string, string> = {
  New: "bg-[#e9f4ec] text-[#245438]",
  Contacted: "bg-[#eef3f8] text-[#255071]",
  Booked: "bg-[#fff6df] text-[#7a5610]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const MESSAGE_STATUS_LABEL: Record<string, string> = {
  New: "待處理",
  Replied: "已回覆",
  Closed: "已結案",
};

const MESSAGE_STATUS_BADGE: Record<string, string> = {
  New: "bg-[#e9f4ec] text-[#245438]",
  Replied: "bg-[#eef3f8] text-[#255071]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const CUSTOMER_SERVICE_STATUS_LABEL: Record<string, string> = {
  New: "新工單",
  InProgress: "處理中",
  WaitingCustomer: "等客戶回覆",
  WaitingInternal: "等內部處理",
  Resolved: "已解決",
  Closed: "已關閉",
};

const CUSTOMER_SERVICE_STATUS_BADGE: Record<string, string> = {
  New: "bg-[#eef2ff] text-[#3342a0]",
  InProgress: "bg-[#e9f4ec] text-[#245438]",
  WaitingCustomer: "bg-[#fff6df] text-[#8a6a2f]",
  WaitingInternal: "bg-[#f3ecdf] text-[#7a5610]",
  Resolved: "bg-[#eaf7ef] text-[#245438]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

function formatCurrency(amount: number): string {
  return `NT$ ${Math.round(amount).toLocaleString()}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return "-";
  return value.slice(0, 16);
}

function formatShortDate(value: string): string {
  const parts = value.split("-");
  return parts.length === 3 ? `${parts[1]}/${parts[2]}` : value;
}

function formatDisplayDate(value: string | null): string {
  if (!value) return "尚未建立";
  const parts = value.split("-");
  return parts.length === 3 ? `${parts[0]}/${parts[1]}/${parts[2]}` : value;
}

function percentChange(current: number, previous: number): { label: string; positive: boolean } | null {
  if (previous <= 0) {
    if (current <= 0) return null;
    return { label: "較上月成長", positive: true };
  }
  const diff = ((current - previous) / previous) * 100;
  const rounded = Math.round(diff * 10) / 10;
  return { label: `${rounded >= 0 ? "+" : ""}${rounded}% 較上月`, positive: rounded >= 0 };
}

function StatusBadge({ label, className }: { label: string; className: string }) {
  return <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-bold ${className}`}>{label}</span>;
}

type AlertItem = {
  key: string;
  icon: ReactNode;
  text: string;
  href: string;
  tone: "warning" | "danger";
};

type CategoryMetric = {
  label: string;
  value: string;
  tone?: "default" | "warning" | "danger";
  /** Optional link target for a metric chip. */
  href?: string;
};

function CategoryCard({
  icon,
  title,
  href,
  metrics,
  tone = "default",
}: {
  icon: ReactNode;
  title: string;
  href: string;
  metrics: CategoryMetric[];
  tone?: "default" | "warning" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "border-[#f2c9c6] bg-[#fdf3f2]"
      : tone === "warning"
        ? "border-[#f0ddb0] bg-[#fdf9ef]"
        : "border-[#dfe6e1] bg-white";
  const chipClass = tone === "default" ? "bg-[#f6f7f5]" : "bg-white";

  return (
    <div className={`flex h-full flex-col rounded-xl border p-4 md:p-5 ${toneClass}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#e9f4ec] text-[#245438]">{icon}</span>
          <h3 className="text-sm font-extrabold text-[#203d32]">{title}</h3>
        </div>
        <Link className="inline-flex items-center gap-1 text-xs font-bold text-[#315746] hover:underline" to={href}>
          查看
          <ArrowRight size={13} />
        </Link>
      </div>
      <div className="grid flex-1 grid-cols-2 gap-2.5">
        {metrics.map((metric) => {
          const valueClass = `mt-1 text-xl font-extrabold ${
            metric.tone === "danger" ? "text-[#9a2d2d]" : metric.tone === "warning" ? "text-[#8a6a2f]" : "text-[#203d32]"
          }`;
          const content = (
            <>
              <p className="text-xs font-bold text-[#7b877f]">{metric.label}</p>
              <p className={valueClass}>{metric.value}</p>
            </>
          );
          return metric.href ? (
            <Link
              className={`flex flex-col justify-center rounded-lg px-3 py-3 transition hover:shadow-[0_6px_16px_rgba(32,61,50,.10)] ${chipClass}`}
              key={metric.label}
              to={metric.href}
            >
              {content}
            </Link>
          ) : (
            <div className={`flex flex-col justify-center rounded-lg px-3 py-3 ${chipClass}`} key={metric.label}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SectionHeader({ icon, iconClass, title, href, linkLabel }: { icon: ReactNode; iconClass: string; title: string; href?: string; linkLabel?: string }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${iconClass}`}>{icon}</span>
        <h2 className="text-base font-extrabold text-[#203d32]">{title}</h2>
      </div>
      {href && (
        <Link className="inline-flex items-center gap-1 text-sm font-bold text-[#315746] hover:underline" to={href}>
          {linkLabel || "查看全部"}
          <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setPageMessage("");
      try {
        const me = await getCurrentAdmin();
        setCurrentUser(me.User);
        const data = await getAdminDashboard();
        setDashboard(data);
      } catch (error) {
        setPageMessage(error instanceof Error ? error.message : "資訊統整表載入失敗。");
        navigate("/admin-login", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  const customerService = dashboard?.CustomerService ?? {
    Status: { New: 0, InProgress: 0, WaitingCustomer: 0, WaitingInternal: 0, Resolved: 0, Closed: 0 },
    OpenTotal: 0,
    Overdue: 0,
  };
  const storeOrders = dashboard?.StoreOrders ?? {
    Total: 0,
    TodayCount: 0,
    ThisMonthCount: 0,
    RevenueThisMonth: 0,
    PendingHeadquarters: 0,
    PendingEngineering: 0,
    WaitingPurchase: 0,
    PreparingOutbound: 0,
    ShippedToStore: 0,
    ReadyForPickup: 0,
    BalanceDue: 0,
    UnnotifiedArrived: 0,
    CompletedThisMonth: 0,
  };
  const alerts: AlertItem[] = [];
  if (dashboard) {
    if (dashboard.ContactMessages.New > 0) {
      alerts.push({
        key: "contact",
        icon: <MessageSquareWarning size={16} />,
        text: `有 ${dashboard.ContactMessages.New} 則未讀聯絡訊息待處理`,
        href: "/admin/contact-messages?status=New",
        tone: "warning",
      });
    }
    if (customerService.Overdue > 0) {
      alerts.push({
        key: "customer-service-overdue",
        icon: <MessageSquareWarning size={16} />,
        text: `有 ${customerService.Overdue} 張客服工單已逾期追蹤`,
        href: "/admin/customer-service",
        tone: "danger",
      });
    } else if (customerService.OpenTotal > 0) {
      alerts.push({
        key: "customer-service-open",
        icon: <MessageSquareWarning size={16} />,
        text: `有 ${customerService.OpenTotal} 張客服工單待處理`,
        href: "/admin/customer-service",
        tone: "warning",
      });
    }
    const fathersDayPending = dashboard.Reservations.FathersDay.New + dashboard.Reservations.FathersDay.Contacted;
    if (fathersDayPending > 0) {
      alerts.push({
        key: "reservations-fathersday",
        icon: <CalendarCheck size={16} />,
        text: `有 ${fathersDayPending} 筆父親節活動預約尚待聯絡或確認`,
        href: "/admin/reservations?status=Pending",
        tone: "warning",
      });
    }
    const productTrialPending = dashboard.Reservations.ProductTrial.New + dashboard.Reservations.ProductTrial.Contacted;
    if (productTrialPending > 0) {
      alerts.push({
        key: "reservations-producttrial",
        icon: <CalendarCheck size={16} />,
        text: `有 ${productTrialPending} 筆試戴預約尚待聯絡或確認`,
        href: "/admin/product-trial-reservations?status=Pending",
        tone: "warning",
      });
    }
    if (dashboard.Orders.CountByStatus.Pending > 0) {
      alerts.push({
        key: "orders-pending",
        icon: <ShoppingBag size={16} />,
        text: `有 ${dashboard.Orders.CountByStatus.Pending} 筆訂單待處理`,
        href: "/admin/orders?status=Pending",
        tone: "warning",
      });
    }
    if (dashboard.Products.OutOfStockCount > 0) {
      alerts.push({
        key: "out-of-stock",
        icon: <PackageX size={16} />,
        text: `有 ${dashboard.Products.OutOfStockCount} 項商品已無庫存`,
        href: "/admin/products?stock=out",
        tone: "danger",
      });
    }
    if (dashboard.Products.LowStockCount > 0) {
      alerts.push({
        key: "low-stock",
        icon: <Package size={16} />,
        text: `有 ${dashboard.Products.LowStockCount} 項商品庫存偏低`,
        href: "/admin/products?stock=low",
        tone: "warning",
      });
    }
    if (storeOrders.PendingHeadquarters > 0) {
      alerts.push({
        key: "store-orders-hq",
        icon: <Receipt size={16} />,
        text: `有 ${storeOrders.PendingHeadquarters} 筆門市訂單待總部確認`,
        href: "/admin/store-sales-orders?Status=PendingHeadquarters",
        tone: "warning",
      });
    }
    if (storeOrders.WaitingPurchase > 0 || storeOrders.PendingEngineering > 0) {
      alerts.push({
        key: "store-orders-procurement",
        icon: <Package size={16} />,
        text: `有 ${storeOrders.WaitingPurchase + storeOrders.PendingEngineering} 筆門市訂單待工程/採購處理`,
        href: "/admin/store-sales-orders?Status=PendingEngineering",
        tone: "warning",
      });
    }
    if (storeOrders.UnnotifiedArrived > 0) {
      alerts.push({
        key: "store-orders-arrived",
        icon: <Package size={16} />,
        text: `有 ${storeOrders.UnnotifiedArrived} 筆門市訂單已到店但尚待通知取件`,
        href: "/admin/store-sales-orders?Status=ShippedToStore",
        tone: "warning",
      });
    }
    if (storeOrders.BalanceDue > 0) {
      alerts.push({
        key: "store-orders-balance",
        icon: <Receipt size={16} />,
        text: `有 ${storeOrders.BalanceDue} 筆門市訂單剩餘待付金額待追蹤`,
        href: "/admin/store-sales-orders?PaymentStatus=BalanceDue",
        tone: "warning",
      });
    }
    if (dashboard.BookingAvailability?.NeedsAttention) {
      const days = dashboard.BookingAvailability.DaysRemaining;
      alerts.push({
        key: "booking-availability",
        icon: <CalendarCheck size={16} />,
        text:
          days === null
            ? "尚未建立前台可預約日期"
            : `前台開放 ${dashboard.BookingAvailability.PublicLookaheadDays} 天，但可預約日期只剩 ${days} 天`,
        href: "/admin/booking-availability",
        tone: "warning",
      });
    }
  }

  const revenueChange = dashboard ? percentChange(dashboard.Orders.RevenueThisMonth, dashboard.Orders.RevenueLastMonth) : null;

  return (
    <AdminLayout onLogout={handleLogout} sectionLabel="資訊統整表" title="總覽" user={currentUser}>
      {loading && <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div>}

      {!loading && pageMessage && (
        <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>
      )}

      {!loading && dashboard && (
        <div className="space-y-6">
          {alerts.length > 0 ? (
            <div className="rounded-xl border border-[#f0ddb0] bg-[#fdf9ef] p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-[#7a5610]">
                <AlertTriangle size={18} />
                待處理事項
              </div>
              <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {alerts.map((alert) => (
                  <Link
                    className={`flex items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2.5 text-sm font-bold transition hover:shadow-[0_8px_20px_rgba(32,61,50,.08)] ${
                      alert.tone === "danger" ? "border-[#f2c9c6] text-[#9a2d2d]" : "border-[#f0ddb0] text-[#7a5610]"
                    }`}
                    key={alert.key}
                    to={alert.href}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                          alert.tone === "danger" ? "bg-[#fdebea]" : "bg-[#fdf3d9]"
                        }`}
                      >
                        {alert.icon}
                      </span>
                      {alert.text}
                    </span>
                    <ArrowRight size={14} />
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-[#cfe3d5] bg-[#f2f9f4] px-4 py-3 text-sm font-bold text-[#245438]">
              <CheckCircle2 size={18} />
              目前沒有待處理事項，一切順利。
            </div>
          )}

          {dashboard.BookingAvailability && (
            <Link
              className={`flex flex-col gap-3 rounded-xl border p-4 transition hover:shadow-[0_10px_24px_rgba(32,61,50,.08)] sm:flex-row sm:items-center sm:justify-between ${
                dashboard.BookingAvailability.NeedsAttention
                  ? "border-[#f0ddb0] bg-[#fff9ed]"
                  : "border-[#cfe3d5] bg-[#f2f9f4]"
              }`}
              to="/admin/booking-availability"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                    dashboard.BookingAvailability.NeedsAttention ? "bg-[#fff1dc] text-[#8a5b14]" : "bg-[#e1f1e7] text-[#245438]"
                  }`}
                >
                  <CalendarCheck size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-extrabold text-[#203d32]">預約日期設定提醒</p>
                  <p className="mt-1 text-sm text-[#59665f]">
                    目前已設定到 {formatDisplayDate(dashboard.BookingAvailability.ConfiguredThrough)}
                    {dashboard.BookingAvailability.DaysRemaining !== null ? `，約剩 ${dashboard.BookingAvailability.DaysRemaining} 天。` : "。"}
                    前台目前開放未來 {dashboard.BookingAvailability.PublicLookaheadDays} 天內預約。
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-[#315746]">
                前往設定
                <ArrowRight size={14} />
              </span>
            </Link>
          )}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <CategoryCard
              href="/admin/orders"
              icon={<ShoppingBag size={18} />}
              metrics={[
                { label: "今日訂單", value: String(dashboard.Orders.TodayCount), href: "/admin/orders" },
                { label: "今日營收", value: formatCurrency(dashboard.Orders.RevenueToday), href: "/admin/orders" },
                {
                  label: "待處理",
                  value: String(dashboard.Orders.CountByStatus.Pending),
                  tone: dashboard.Orders.CountByStatus.Pending > 0 ? "warning" : "default",
                  href: "/admin/orders?status=Pending",
                },
                { label: "處理中", value: String(dashboard.Orders.CountByStatus.Processing), href: "/admin/orders?status=Processing" },
              ]}
              title="訂單總覽"
              tone={dashboard.Orders.CountByStatus.Pending > 0 ? "warning" : "default"}
            />
            <CategoryCard
              href="/admin/reservations"
              icon={<CalendarCheck size={18} />}
              metrics={[
                { label: "待處理預約", value: String(dashboard.Reservations.PendingTotal), tone: dashboard.Reservations.PendingTotal > 0 ? "warning" : "default" },
                {
                  label: "父親節待處理",
                  value: String(dashboard.Reservations.FathersDay.New + dashboard.Reservations.FathersDay.Contacted),
                  href: "/admin/reservations?status=Pending",
                },
                {
                  label: "試戴待處理",
                  value: String(dashboard.Reservations.ProductTrial.New + dashboard.Reservations.ProductTrial.Contacted),
                  href: "/admin/product-trial-reservations?status=Pending",
                },
                {
                  label: "未讀聯絡訊息",
                  value: String(dashboard.ContactMessages.New),
                  tone: dashboard.ContactMessages.New > 0 ? "warning" : "default",
                  href: "/admin/contact-messages?status=New",
                },
              ]}
              title="預約與客服"
              tone={dashboard.Reservations.PendingTotal > 0 ? "warning" : "default"}
            />
            <CategoryCard
              href="/admin/store-sales-orders"
              icon={<Receipt size={18} />}
              metrics={[
                { label: "今日門市訂單", value: String(storeOrders.TodayCount), href: "/admin/store-sales-orders" },
                { label: "本月成交", value: formatCurrency(storeOrders.RevenueThisMonth), href: "/admin/store-sales-orders" },
                {
                  label: "待總部確認",
                  value: String(storeOrders.PendingHeadquarters),
                  tone: storeOrders.PendingHeadquarters > 0 ? "warning" : "default",
                  href: "/admin/store-sales-orders?Status=PendingHeadquarters",
                },
                {
                  label: "待工程/採購",
                  value: String(storeOrders.PendingEngineering + storeOrders.WaitingPurchase),
                  tone: storeOrders.PendingEngineering + storeOrders.WaitingPurchase > 0 ? "warning" : "default",
                  href: "/admin/store-sales-orders?Status=PendingEngineering",
                },
                {
                  label: "可通知取件",
                  value: String(storeOrders.UnnotifiedArrived + storeOrders.ReadyForPickup),
                  tone: storeOrders.UnnotifiedArrived + storeOrders.ReadyForPickup > 0 ? "warning" : "default",
                  href: "/admin/store-sales-orders?Status=ShippedToStore",
                },
                {
                  label: "剩餘待付",
                  value: String(storeOrders.BalanceDue),
                  tone: storeOrders.BalanceDue > 0 ? "warning" : "default",
                  href: "/admin/store-sales-orders?PaymentStatus=BalanceDue",
                },
              ]}
              title="門市訂單"
              tone={storeOrders.PendingHeadquarters + storeOrders.PendingEngineering + storeOrders.WaitingPurchase + storeOrders.BalanceDue > 0 ? "warning" : "default"}
            />
            <CategoryCard
              href="/admin/customer-service"
              icon={<MessageSquareWarning size={18} />}
              metrics={[
                {
                  label: "待處理工單",
                  value: String(customerService.OpenTotal),
                  tone: customerService.OpenTotal > 0 ? "warning" : "default",
                  href: "/admin/customer-service",
                },
                {
                  label: "新工單",
                  value: String(customerService.Status.New),
                  href: "/admin/customer-service",
                },
                {
                  label: "處理中",
                  value: String(customerService.Status.InProgress + customerService.Status.WaitingCustomer + customerService.Status.WaitingInternal),
                  href: "/admin/customer-service",
                },
                {
                  label: "逾期追蹤",
                  value: String(customerService.Overdue),
                  tone: customerService.Overdue > 0 ? "danger" : "default",
                  href: "/admin/customer-service",
                },
              ]}
              title="客服中心"
              tone={customerService.Overdue > 0 ? "danger" : customerService.OpenTotal > 0 ? "warning" : "default"}
            />
            <CategoryCard
              href="/admin/members"
              icon={<Users size={18} />}
              metrics={[
                { label: "會員總數", value: String(dashboard.Members.Total), href: "/admin/members" },
                { label: "本月新增", value: String(dashboard.Members.NewThisMonth), href: "/admin/members" },
                { label: "電子報訂閱", value: String(dashboard.Newsletter.SubscriberCounts.Active), href: "/admin/newsletter-subscribers" },
                { label: "本週新增", value: String(dashboard.Members.NewThisWeek), href: "/admin/members" },
              ]}
              title="會員與電子報"
            />
            <CategoryCard
              href="/admin/products"
              icon={<Package size={18} />}
              metrics={[
                { label: "上架商品", value: String(dashboard.Products.StatusCounts.Published), href: "/admin/products?status=Published" },
                { label: "下架商品", value: String(dashboard.Products.StatusCounts.Hidden), href: "/admin/products?status=Hidden" },
                {
                  label: "低庫存",
                  value: String(dashboard.Products.LowStockCount),
                  tone: dashboard.Products.LowStockCount > 0 ? "warning" : "default",
                  href: "/admin/products?stock=low",
                },
                {
                  label: "已無庫存",
                  value: String(dashboard.Products.OutOfStockCount),
                  tone: dashboard.Products.OutOfStockCount > 0 ? "danger" : "default",
                  href: "/admin/products?stock=out",
                },
              ]}
              title="商品與庫存"
              tone={dashboard.Products.OutOfStockCount > 0 ? "danger" : dashboard.Products.LowStockCount > 0 ? "warning" : "default"}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.6fr_1fr]">
            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader
                href="/admin/orders"
                icon={revenueChange && !revenueChange.positive ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                iconClass="bg-[#e6eefb] text-[#2a5ca8]"
                linkLabel="查看所有訂單"
                title="近 14 天營收趨勢"
              />
              {revenueChange && (
                <p className={`-mt-2 mb-3 text-xs font-bold ${revenueChange.positive ? "text-[#245438]" : "text-[#9a2d2d]"}`}>
                  本月營收 {formatCurrency(dashboard.Orders.RevenueThisMonth)} ・ {revenueChange.label}
                </p>
              )}
              <div className="h-[280px] w-full">
                <ResponsiveContainer height="100%" width="100%">
                  <AreaChart data={dashboard.Orders.Trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="revenueFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="5%" stopColor="#315746" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#315746" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#edf0ee" vertical={false} />
                    <XAxis dataKey="Date" stroke="#7b877f" style={{ fontSize: 12 }} tickFormatter={(value: string) => formatShortDate(value)} />
                    <YAxis stroke="#7b877f" style={{ fontSize: 12 }} tickFormatter={(value: number) => `${(value / 1000).toFixed(0)}k`} width={44} />
                    <Tooltip
                      formatter={(value: number, name: string) => [name === "Revenue" ? formatCurrency(value) : value, name === "Revenue" ? "營收" : "訂單數"]}
                      labelFormatter={(label: string) => label}
                    />
                    <Area dataKey="Revenue" fill="url(#revenueFill)" stroke="#315746" strokeWidth={2} type="monotone" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader icon={<Receipt size={16} />} iconClass="bg-[#e9f4ec] text-[#245438]" title="訂單狀態分布" />
              <div className="space-y-3">
                {(Object.keys(dashboard.Orders.CountByStatus) as Array<keyof typeof dashboard.Orders.CountByStatus>).map((status) => {
                  const total = Object.values(dashboard.Orders.CountByStatus).reduce((sum, n) => sum + n, 0) || 1;
                  const count = dashboard.Orders.CountByStatus[status];
                  const percent = Math.round((count / total) * 100);
                  return (
                    <div key={status}>
                      <div className="mb-1 flex items-center justify-between text-xs font-bold text-[#59665f]">
                        <span>{ORDER_STATUS_LABEL[status] || status}</span>
                        <span>{count} 筆</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-[#f0f5f2]">
                        <div className={`h-full rounded-full ${ORDER_STATUS_BAR[status] || "bg-[#315746]"}`} style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-5 border-t border-[#edf0ee] pt-4">
                <div className="mb-2 flex items-center gap-2 text-sm font-extrabold text-[#203d32]">
                  <UserPlus size={16} />
                  會員成長
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-md bg-[#f6f7f5] py-2">
                    <p className="text-lg font-extrabold text-[#203d32]">{dashboard.Members.NewToday}</p>
                    <p className="text-xs font-bold text-[#7b877f]">今日新增</p>
                  </div>
                  <div className="rounded-md bg-[#f6f7f5] py-2">
                    <p className="text-lg font-extrabold text-[#203d32]">{dashboard.Members.NewThisWeek}</p>
                    <p className="text-xs font-bold text-[#7b877f]">本週新增</p>
                  </div>
                  <div className="rounded-md bg-[#f6f7f5] py-2">
                    <p className="text-lg font-extrabold text-[#203d32]">{dashboard.Members.NewThisMonth}</p>
                    <p className="text-xs font-bold text-[#7b877f]">本月新增</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/orders" icon={<ShoppingBag size={16} />} iconClass="bg-[#e6eefb] text-[#2a5ca8]" title="最新訂單" />
              {dashboard.Recent.Orders.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無訂單資料。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {dashboard.Recent.Orders.map((order) => (
                    <Link className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]" key={order.OrderId} to={`/admin/orders/${order.OrderId}`}>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{order.OrderNumber}</p>
                        <p className="truncate text-xs text-[#7b877f]">
                          {order.RecipientName} ・ {formatDateTime(order.CreatedAt)}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-sm font-extrabold text-[#203d32]">{formatCurrency(order.TotalAmount)}</p>
                        <StatusBadge className={ORDER_STATUS_BADGE[order.OrderStatus] || "bg-[#eeeeee] text-[#59635d]"} label={ORDER_STATUS_LABEL[order.OrderStatus] || order.OrderStatus} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/store-sales-orders" icon={<Receipt size={16} />} iconClass="bg-[#e9f4ec] text-[#245438]" title="最新門市訂單" />
              {(dashboard.Recent.StoreSalesOrders ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無門市訂單資料。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {(dashboard.Recent.StoreSalesOrders ?? []).map((order) => (
                    <Link
                      className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]"
                      key={order.StoreSalesOrderId}
                      to={`/admin/store-sales-orders?Keyword=${encodeURIComponent(order.OrderNumber)}`}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{order.OrderNumber}</p>
                        <p className="truncate text-xs text-[#7b877f]">
                          {order.LocationName} ・ {order.CustomerName} ・ {order.FirstProductName || "未帶商品"}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-sm font-extrabold text-[#203d32]">{formatCurrency(order.TotalAmount)}</p>
                        <StatusBadge
                          className={order.Status === "Completed" ? "bg-[#e9f4ec] text-[#245438]" : order.Status === "Voided" ? "bg-[#fdebea] text-[#9a2d2d]" : "bg-[#fff6df] text-[#7a5610]"}
                          label={order.StatusLabel}
                        />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/contact-messages" icon={<Mail size={16} />} iconClass="bg-[#fff6df] text-[#7a5610]" title="最新聯絡訊息" />
              {dashboard.Recent.ContactMessages.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無聯絡訊息。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {dashboard.Recent.ContactMessages.map((message) => (
                    <Link className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]" key={message.ContactMessageId} to="/admin/contact-messages">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{message.Name}</p>
                        <p className="truncate text-xs text-[#7b877f]">{message.Subject}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-xs font-bold text-[#7b877f]">{formatDateTime(message.CreatedAt)}</p>
                        <StatusBadge className={MESSAGE_STATUS_BADGE[message.Status] || "bg-[#eeeeee] text-[#59635d]"} label={MESSAGE_STATUS_LABEL[message.Status] || message.Status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>


            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/customer-service" icon={<MessageSquareWarning size={16} />} iconClass="bg-[#e9f4ec] text-[#245438]" title="最新客服工單" />
              {(dashboard.Recent.CustomerServiceTickets ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無客服工單。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {(dashboard.Recent.CustomerServiceTickets ?? []).map((ticket) => (
                    <Link className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]" key={ticket.TicketId} to="/admin/customer-service">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{ticket.TicketNumber}</p>
                        <p className="truncate text-xs text-[#7b877f]">
                          {ticket.Subject} ・ {ticket.CustomerName || "未填姓名"}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-xs font-bold text-[#7b877f]">{formatDateTime(ticket.UpdatedAt)}</p>
                        <StatusBadge className={CUSTOMER_SERVICE_STATUS_BADGE[ticket.Status] || "bg-[#eeeeee] text-[#59635d]"} label={CUSTOMER_SERVICE_STATUS_LABEL[ticket.Status] || ticket.Status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/reservations" icon={<CalendarCheck size={16} />} iconClass="bg-[#fff6df] text-[#7a5610]" title="最新父親節活動預約" />
              {dashboard.Recent.FathersDayReservations.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無預約資料。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {dashboard.Recent.FathersDayReservations.map((item) => (
                    <Link className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]" key={item.ReservationId} to="/admin/reservations">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{item.Name}</p>
                        <p className="truncate text-xs text-[#7b877f]">{item.Store}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-xs font-bold text-[#7b877f]">{formatDateTime(item.CreatedAt)}</p>
                        <StatusBadge className={RESERVATION_STATUS_BADGE[item.Status] || "bg-[#eeeeee] text-[#59635d]"} label={RESERVATION_STATUS_LABEL[item.Status] || item.Status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-[#dfe6e1] bg-white p-4 md:p-5">
              <SectionHeader href="/admin/product-trial-reservations" icon={<Sparkles size={16} />} iconClass="bg-[#fff6df] text-[#7a5610]" title="最新試戴預約" />
              {dashboard.Recent.ProductTrialReservations.length === 0 ? (
                <p className="py-6 text-center text-sm text-[#7b877f]">尚無預約資料。</p>
              ) : (
                <div className="divide-y divide-[#edf0ee]">
                  {dashboard.Recent.ProductTrialReservations.map((item) => (
                    <Link
                      className="flex items-center justify-between gap-3 py-3 hover:bg-[#f7faf8]"
                      key={item.ProductTrialReservationId}
                      to="/admin/product-trial-reservations"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-[#203d32]">{item.Name}</p>
                        <p className="truncate text-xs text-[#7b877f]">{item.ProductName || item.Store}</p>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <p className="text-xs font-bold text-[#7b877f]">{formatDateTime(item.CreatedAt)}</p>
                        <StatusBadge className={RESERVATION_STATUS_BADGE[item.Status] || "bg-[#eeeeee] text-[#59635d]"} label={RESERVATION_STATUS_LABEL[item.Status] || item.Status} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
